import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";
import { down, hoverOnly, layout, reducedMotion } from "../../styles/breakpoints";

const useStyles = createUseStyles((_) => ({
  /* ----------------------------------------------------------------------
     Shell
     ---------------------------------------------------------------------- */

  /**
   * Outer wrapper. A flex column so the mobile top bar and the page content
   * stack, and the content area gets the exact remaining height. This replaces
   * the per-page `height: calc(100vh - 60px)` magic numbers, which were wrong
   * as soon as the header height changed.
   */
  shell: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "var(--app-height)",
    overflow: "hidden",
    boxSizing: "border-box",
    /* Animates the inline `padding-left` that offsets the content against the
       rail/expanded sidebar. */
    transition: "padding-left 200ms ease",
    [reducedMotion]: {
      transition: "none",
    },
    [down.md]: {
      /* Content is full width; the drawer floats above it. */
      paddingLeft: 0,
    },
  },

  /* ----------------------------------------------------------------------
     Mobile top bar — only rendered below the `md` breakpoint.
     ---------------------------------------------------------------------- */

  mobileBar: {
    display: "none",
    [down.md]: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      position: "sticky",
      top: 0,
      zIndex: "var(--z-sticky-header)",
      height: layout.mobileBarHeight,
      padding: "0 12px",
      backgroundColor: colors.secondary,
      color: colors.WHITE,
    },
  },

  burger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    /* 44px is the minimum comfortable tap target. */
    width: 44,
    height: 44,
    flexShrink: 0,
    border: "none",
    borderRadius: 12,
    backgroundColor: "transparent",
    color: colors.WHITE,
    cursor: "pointer",
    padding: 0,
    "&:active": {
      backgroundColor: "rgba(255, 255, 255, 0.18)",
    },
    [hoverOnly]: {
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.14)",
      },
    },
  },

  mobileTitle: {
    fontFamily: "Poppins-Bold",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    /* Long page titles truncate rather than pushing the burger off-screen. */
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  /* ----------------------------------------------------------------------
     Sidebar
     ---------------------------------------------------------------------- */

  /**
   * `fixed` rather than `absolute`: the sidebar must stay put while the page
   * body scrolls. The previous `absolute` positioning let it scroll away on
   * long pages.
   */
  drawerPositioner: {
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: "var(--z-sidebar)",
    [down.md]: {
      /* Off-canvas by default on small screens. */
      transform: "translateX(-100%)",
      transition: "transform 220ms ease",
      [reducedMotion]: {
        transition: "none",
      },
    },
  },

  drawerOpen: {
    [down.md]: {
      transform: "translateX(0)",
      boxShadow: "0 0 24px rgba(0, 0, 0, 0.28)",
    },
  },

  /** Dimmed layer behind the mobile drawer. Tapping it closes the drawer. */
  backdrop: {
    display: "none",
    [down.md]: {
      display: "block",
      position: "fixed",
      inset: 0,
      zIndex: "var(--z-backdrop)",
      backgroundColor: "rgba(0, 0, 0, 0.45)",
      border: "none",
      padding: 0,
      cursor: "pointer",
      animation: "$fadeIn 180ms ease",
    },
  },

  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },

  container: {
    backgroundColor: colors.secondary,
    height: "var(--app-height)",
    padding: "16px 0",
    position: "relative",
    overflowY: "auto",
    overflowX: "hidden",
    transition: "width 200ms ease",
    /* Keep the rail usable on very short viewports / landscape phones. */
    display: "flex",
    flexDirection: "column",
    [reducedMotion]: {
      transition: "none",
    },
    [down.md]: {
      /* The drawer always shows labels — an icon rail is not useful on a
         phone where there is no hover to reveal a tooltip. Sidebar.tsx omits
         the inline width below `md`, so this no longer needs `!important`. */
      width: layout.sidebarExpanded,
      /* Clear the notch / home indicator on iOS. */
      paddingTop: "max(16px, env(safe-area-inset-top))",
      paddingBottom: "max(16px, env(safe-area-inset-bottom))",
    },
  },

  menuItemContainer: {
    display: "flex",
    justifyContent: "space-between",
    flex: "1 1 auto",
    minHeight: 0,
    flexDirection: "column",
    gap: 24,
    alignItems: "stretch",
    padding: "0 12px",
  },

  menuGroup: {
    display: "flex",
    flexDirection: "column",
  },

  /** Hairline between the navigation items and the account/logout group. */
  menuGroupBottom: {
    display: "flex",
    flexDirection: "column",
    paddingTop: 12,
    borderTop: "1px solid rgba(255, 255, 255, 0.22)",
  },

  /* ----------------------------------------------------------------------
     Page content
     ---------------------------------------------------------------------- */

  content: {
    position: "relative",
    /* Fills the height left over by the mobile bar and scrolls internally. */
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0, // lets flex/grid children shrink instead of overflowing
    overflowY: "auto",
    /*
     * `hidden` here meant that anything wider than the content box was CLIPPED
     * and unreachable — at browser zoom above 100%, or on a narrow desktop
     * window with the sidebar expanded, the primary action button in a page
     * header would simply vanish off the right edge with no way to scroll to
     * it. `auto` degrades to a scrollbar instead of swallowing the control.
     */
    overflowX: "auto",
    /* The sidebar offset moved to the shell's padding-left, so there is no
       margin here to animate or to reset at the `md` breakpoint. */
  },
}));

export default useStyles;
